import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { serviceCatalogCostsQuery, serviceCatalogFormQuery } from '../api/serviceCatalog'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { CatalogHead, CatalogTabs } from './ServiceCatalogChrome'
import { CostsPanel } from './ServiceCatalogCosts'
import { ServiceCatalogForm } from './ServiceCatalogForm'

/**
 * Cele doua adrese care in portal deschideau NUMAI o fereastra:
 * `service_catalog/costs/{id}` si `service_catalog/form/{id}`.
 *
 * DE CE AU PROPRIA PAGINA. Nu sunt adrese moarte - le scrie cineva in alte
 * ecrane, si duc acolo din alt modul:
 *
 *   - fisa unui element din CMDB trimite la `service_catalog/costs/{id}`
 *     ("Cost rules", intr-o fila noua) - vezi `inventoree/cmdb/view.php:598`;
 *   - fisa unui tip de tichet din setarile Easeedesk trimite la
 *     `service_catalog/form/{id}` - vezi `easeedesk_settings/tickets/
 *     ticket_types/overview/index.php:2011`;
 *   - tabloul de costuri trimite tot la `costs/{id}`.
 *
 * In portal, deschise direct, raspundeau cu bucata de HTML a ferestrei si fara
 * pagina in jur - adica un ecran alb cu un formular in coltul din stanga sus.
 * Aici primesc cap de pagina si banda de file, ca omul care ajunge dintr-o
 * legatura sa stie unde a nimerit si sa se poata intoarce.
 */
export function ServiceCatalogCostsEntryPage() {
  const { id } = useParams({ from: '/service_catalog/costs/$id' })
  const entryId = Number(id)

  const page = useQuery(serviceCatalogCostsQuery(entryId))
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(
    ['css/adm.css', 'css/service_shop.css', 'css/service_catalog.css'],
    shell.data?.version ?? '',
  )

  if (isForbidden(page.error)) return <Forbidden />
  if (!page.data) return <Spinner />

  return (
    <div id="content">
      <CatalogHead
        title={page.data.entry.name}
        icon="fa-coins"
        tone="blue-mauve"
        note="what this service costs — several rules add up, and a yearly figure counts as a twelfth in any one month"
        actions={
          <a className="btn btn-default btn-sm" href="/app/service_catalog">
            <i className="fas fa-arrow-left"></i> The catalogue
          </a>
        }
        stats={[
          { value: page.data.rules.length, label: 'cost rules', tone: page.data.rules.length ? 'blue-mauve' : 'grey' },
          { value: page.data.entry.kind === 4 ? 'Business' : 'IT', label: 'service', tone: 'grey' },
        ]}
      />

      <CatalogTabs active="catalog" />

      <div className="adm-card adm-tone-blue-mauve">
        <CostsPanel page={page.data} entryId={entryId} />
      </div>
    </div>
  )
}

/** `service_catalog/form/{id}`: acelasi formular, deschis singur. */
export function ServiceCatalogFormPage() {
  const { id } = useParams({ from: '/service_catalog/form/$id' })
  const entryId = Number(id)

  const form = useQuery(serviceCatalogFormQuery(entryId))
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(
    ['css/adm.css', 'css/service_shop.css', 'css/service_catalog.css'],
    shell.data?.version ?? '',
  )

  if (isForbidden(form.error)) return <Forbidden />
  if (!form.data) return <Spinner />

  const back = () => {
    window.location.assign('/app/service_catalog')
  }

  return (
    <div id="content">
      <CatalogHead
        title={form.data.entry?.name ?? 'Add a service to the catalogue'}
        icon="fa-concierge-bell"
        tone="blue-mauve"
        note="the description, the picture, who it is published to, and the ticket a request opens"
        actions={
          <a className="btn btn-default btn-sm" href="/app/service_catalog">
            <i className="fas fa-arrow-left"></i> The catalogue
          </a>
        }
        stats={
          form.data.entry
            ? [{ value: form.data.entry.kind === 4 ? 'Business' : 'IT', label: 'service', tone: 'grey' }]
            : []
        }
      />

      <CatalogTabs active="catalog" />

      <div className="adm-card adm-tone-blue-mauve">
        <ServiceCatalogForm id={entryId} onSaved={back} onClose={back} />
      </div>
    </div>
  )
}
